import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { deleteDoc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase.config.js';
import { useAgency } from '../AgencyContext';
import { Assignment, Add, Edit, Delete, MoreVert, CallSplit, AutoAwesome } from '@mui/icons-material';
import AddTaskModal from './AddTaskModal';
import EditTaskModal from './EditTaskModal';
import './PromptAutomatorLeftPanel.css';

const TaskSkeleton = () => (
  <div className="task-skeleton">
    <div className="task-skeleton-photo"></div>
    <div className="task-skeleton-label"></div>
    <div className="task-skeleton-badge"></div>
  </div>
);

function PromptAutomatorLeftPanel({ 
  tasks, 
  selectedTask, 
  setSelectedTask, 
  setSelectedPromptType, 
  setCurrentPromptValue, 
  setGeneratedPrompt,
  loadingTasks,
  selectedModel,
  aiModels,
  refreshTasks,
  setNotification,
  onEditTaskFromHeader
}) {
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
  const [editTaskModalOpen, setEditTaskModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [deleteTaskModalOpen, setDeleteTaskModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [duplicateTaskModalOpen, setDuplicateTaskModalOpen] = useState(false);
  const [taskToDuplicate, setTaskToDuplicate] = useState(null);
  const [taskDropdownOpen, setTaskDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [user] = useAuthState(auth);
  const { getAgencyCollection, getAgencyDoc } = useAgency();
  const dropdownTriggerRefs = useRef({});

  const handleTaskSelect = (task) => {
    setSelectedTask(task);
    setSelectedPromptType({ id: 'promptTemplate', label: 'Prompt', icon: 'Settings' });
    setCurrentPromptValue('');
    setGeneratedPrompt('');
  };

  const getTotalPromptsCount = (task) => {
    if (!task.prompts) return 0;
    
    let total = 0;
    Object.entries(task.prompts).forEach(([key, value]) => {
      if (key === 'promptTemplate' || key === 'personalityTemplate') {
        if (typeof value === 'string' && value.length > 0) {
          total += 1;
        }
      } else {
        if (Array.isArray(value)) {
          total += value.length;
        }
      }
    });
    
    return total;
  };

  const handleTaskDropdownToggle = (e, taskId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (taskDropdownOpen === taskId) {
      setTaskDropdownOpen(null);
    } else {
      const triggerElement = dropdownTriggerRefs.current[taskId];
      if (triggerElement) {
        const rect = triggerElement.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.right + 4
        });
      }
      setTaskDropdownOpen(taskId);
    }
  };

  const handleTaskEdit = (e, task) => {
    e.preventDefault();
    e.stopPropagation();
    setTaskDropdownOpen(null);
    setTimeout(() => {
      setTaskToEdit(task);
      setEditTaskModalOpen(true);
    }, 50);
  };

  // Expose edit function for header
  const handleEditTaskFromHeaderInternal = (task) => {
    setTaskToEdit(task);
    setEditTaskModalOpen(true);
  };

  // Pass the edit function to parent if callback provided
  React.useEffect(() => {
    if (onEditTaskFromHeader) {
      onEditTaskFromHeader.current = handleEditTaskFromHeaderInternal;
    }
  }, [onEditTaskFromHeader]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (taskDropdownOpen) {
        const dropdownElement = document.querySelector('.task-options-dropdown-portal');
        const triggerElement = dropdownTriggerRefs.current[taskDropdownOpen];
        
        if (dropdownElement && !dropdownElement.contains(event.target) && 
            triggerElement && !triggerElement.contains(event.target)) {
          setTaskDropdownOpen(null);
        }
      }
    };

    if (taskDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [taskDropdownOpen]);

  const handleTaskDelete = (e, task) => {
    e.preventDefault();
    e.stopPropagation();
    setTaskDropdownOpen(null);
    setTimeout(() => {
      setTaskToDelete(task);
      setDeleteTaskModalOpen(true);
    }, 50);
  };

  const handleTaskDuplicate = (e, task) => {
    e.preventDefault();
    e.stopPropagation();
    setTaskDropdownOpen(null);
    setTimeout(() => {
      setTaskToDuplicate(task);
      setDuplicateTaskModalOpen(true);
    }, 50);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      const taskDocRef = getAgencyDoc('aitask', taskToDelete.id);
      if (!taskDocRef) return;
      
      await deleteDoc(taskDocRef);
      
      if (selectedTask && selectedTask.id === taskToDelete.id) {
        setSelectedTask(null);
        setSelectedPromptType(null);
        setCurrentPromptValue('');
      }
      
      setDeleteTaskModalOpen(false);
      setTaskToDelete(null);
      setNotification('🗑️ Task deleted successfully');
      setTimeout(() => setNotification(''), 3000);
      await refreshTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      setNotification('❌ Failed to delete task');
      setTimeout(() => setNotification(''), 3000);
    }
  };

  const confirmDuplicateTask = async () => {
    if (!taskToDuplicate || !user) return;
    
    try {
      const aitaskCollection = getAgencyCollection('aitask');
      if (!aitaskCollection) return;

      const duplicatedTask = {
        ...taskToDuplicate,
        name: `${taskToDuplicate.name} (Copy)`,
        createdAt: serverTimestamp(),
        createdBy: user.email,
        lastModified: serverTimestamp(),
        lastModifiedBy: user.email
      };

      delete duplicatedTask.id;
      
      await addDoc(aitaskCollection, duplicatedTask);
      
      setDuplicateTaskModalOpen(false);
      setTaskToDuplicate(null);
      setNotification('Task duplicated successfully');
      setTimeout(() => setNotification(''), 3000);
      await refreshTasks();
    } catch (error) {
      console.error('Error duplicating task:', error);
      setNotification('❌ Failed to duplicate task');
      setTimeout(() => setNotification(''), 3000);
    }
  };

  const handleDragStart = (e, task, index) => {
    setDraggedTask({ task, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    
    // Add drag styling with delay to avoid flickering
    setTimeout(() => {
      e.target.classList.add('dragging');
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedTask(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedTask && draggedTask.index !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    // Only remove drag over if we're leaving the task area completely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (!draggedTask || draggedTask.index === dropIndex) {
      setDraggedTask(null);
      return;
    }

    // INSTANT: Update UI immediately for smooth UX
    const newTasks = [...tasks];
    const [movedTask] = newTasks.splice(draggedTask.index, 1);
    newTasks.splice(dropIndex, 0, movedTask);
    
    // Update local state instantly
    if (typeof refreshTasks === 'function' && refreshTasks.setTasksDirectly) {
      refreshTasks.setTasksDirectly(newTasks);
    }
    
    setDraggedTask(null);

    // BACKGROUND: Update Firebase without blocking UI
    try {
      const updatePromises = newTasks.map(async (task, index) => {
        const taskDocRef = getAgencyDoc('aitask', task.id);
        if (taskDocRef) {
          return updateDoc(taskDocRef, {
            order: index,
            lastModified: serverTimestamp(),
            lastModifiedBy: user.email
          });
        }
      });

      await Promise.all(updatePromises);
      // Optionally refresh from Firebase to ensure sync
      setTimeout(() => {
        refreshTasks();
      }, 100);
      
    } catch (error) {
      console.error('Error reordering tasks:', error);
      // On error, refresh to restore correct order
      await refreshTasks();
    }
  };

  return (
    <div className="left-panel">
      <div className="left-panel__header">
        <h2 className="panel-title">Your Tasks</h2>
        <button 
          onClick={() => setAddTaskModalOpen(true)}
          className="create-task-btn"
          title="Create new task"
        >
          <Add className="create-btn-icon" />
          <span className="create-btn-text">Create</span>
        </button>
      </div>
      
      <nav className="left-panel__nav">
        {loadingTasks ? (
          <>
            <TaskSkeleton />
            <TaskSkeleton />
            <TaskSkeleton />
          </>
        ) : (
          <>
{tasks.map((task, index) => (
              <button
                key={task.id}
                className={`nav-item ${selectedTask?.id === task.id ? 'nav-item--active' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                onClick={() => handleTaskSelect(task)}
                draggable={true}
                onDragStart={(e) => handleDragStart(e, task, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
              >
                <div className="nav-item__content">
                  {task.photoUrl ? (
                    <img 
                      src={task.photoUrl} 
                      alt={task.name}
                      className="nav-item__photo"
                    />
                  ) : (
                    <div className="nav-item__photo-placeholder">
                      <AutoAwesome className="nav-item__photo-icon" />
                    </div>
                  )}
                  <span className="nav-item__label">{task.name}</span>
                </div>
                <div className="badge-container">
                  <div className="prompt-count-badge">
                    {getTotalPromptsCount(task) || 0}
                  </div>
                  <div className="more-options-badge">
                    <button
                      ref={(el) => { dropdownTriggerRefs.current[task.id] = el; }}
                      className="more-options-btn"
                      onClick={(e) => handleTaskDropdownToggle(e, task.id)}
                      title="Task options"
                    >
                      <MoreVert style={{fontSize: 14}}/>
                    </button>
                  </div>
                </div>
              </button>
            ))}
            
            {/* Drop zone for last position */}
            {tasks.length > 0 && (
              <div
                className={`drop-zone-last ${dragOverIndex === tasks.length ? 'drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, tasks.length)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, tasks.length)}
              />
            )}
            
            {tasks.length === 0 && (
              <div className="empty-state">
                <div className="empty-state__icon">
                  <Assignment />
                </div>
                <p className="empty-state__message">No tasks yet</p>
              </div>
            )}
          </>
        )}
      </nav>

      {/* Modals */}
      <AddTaskModal 
        isOpen={addTaskModalOpen}
        onClose={() => setAddTaskModalOpen(false)}
        selectedModel={selectedModel}
        aiModels={aiModels}
        onTaskCreated={() => {
          refreshTasks();
          setNotification('✅ Task created successfully');
          setTimeout(() => setNotification(''), 3000);
        }}
      />

      <EditTaskModal 
        isOpen={editTaskModalOpen}
        onClose={() => {
          setEditTaskModalOpen(false);
          setTaskToEdit(null);
        }}
        task={taskToEdit}
        onTaskUpdated={() => {
          refreshTasks();
          setNotification('Task updated successfully');
          setTimeout(() => setNotification(''), 3000);
        }}
      />

      {/* Delete Task Modal */}
      {deleteTaskModalOpen && (
        <div className="modal__overlay" onClick={() => setDeleteTaskModalOpen(false)}>
          <div className="modal__content" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <Delete />
              <h3 className="modal__title">Delete Task</h3>
            </div>
            <div className="modal__body">
              <p className="modal__message">
                Are you sure you want to delete <strong>"{taskToDelete?.name}"</strong>?
              </p>
              <p className="modal__warning">
                This will also delete all associated prompts. This action cannot be undone.
              </p>
            </div>
            <div className="modal__actions">
              <button 
                onClick={() => setDeleteTaskModalOpen(false)}
                className="modal__button modal__button--cancel"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDeleteTask}
                className="modal__button modal__button--delete"
              >
                Delete Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Task Modal */}
      {duplicateTaskModalOpen && (
        <div className="modal__overlay" onClick={() => setDuplicateTaskModalOpen(false)}>
          <div className="modal__content" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <CallSplit />
              <h3 className="modal__title">Duplicate Task</h3>
            </div>
            <div className="modal__body">
              <p className="modal__message">
                Are you sure you want to duplicate <strong>"{taskToDuplicate?.name}"</strong>?
              </p>
              <p className="modal__warning__cool">
                This will create an exact copy of the task with all its prompts and settings.
              </p>
            </div>
            <div className="modal__actions">
              <button 
                onClick={() => setDuplicateTaskModalOpen(false)}
                className="modal__button modal__button--cancel"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDuplicateTask}
                className="modal__button modal__button--confirm"
              >
                Duplicate Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Options Dropdown Portal */}
      {taskDropdownOpen && createPortal(
        <div 
          className="task-options-dropdown-portal"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <div className="task-options-dropdown">
            <button
              className="dropdown-item dropdown-item--edit"
              onClick={(e) => handleTaskEdit(e, tasks.find(t => t.id === taskDropdownOpen))}
            >
              <Edit />
              <span>Edit</span>
            </button>
            <button
              className="dropdown-item dropdown-item--duplicate"
              onClick={(e) => handleTaskDuplicate(e, tasks.find(t => t.id === taskDropdownOpen))}
            >
              <CallSplit />
              <span>Duplicate</span>
            </button>
            <button
              className="dropdown-item dropdown-item--delete"
              onClick={(e) => handleTaskDelete(e, tasks.find(t => t.id === taskDropdownOpen))}
            >
              <Delete />
              <span>Delete</span>
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default PromptAutomatorLeftPanel;